import React, { useEffect, useMemo } from "react";

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("pt-BR");
}

function buildMapPoints(groups, selectedIds) {
  const selected = new Set(selectedIds);

  return groups
    .filter((group) =>
      (group.pedidos || []).some((pedido) => selected.has(pedido.IDpedido))
    )
    .map((group) => {
      const latitude = Number(group?.coordenadas?.latitude);
      const longitude = Number(group?.coordenadas?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }

      return {
        id: `${group.IDcliente}:${group.IDendereco}`,
        position: [latitude, longitude],
        title: group.nomeCliente || "Cliente",
        subtitle: group.enderecoFormatado || "",
        orders: (group.pedidos || []).filter((pedido) =>
          selected.has(pedido.IDpedido)
        ).length,
      };
    })
    .filter(Boolean);
}

export default function OrderSelectionPanel({
  routeName,
  citySelection,
  onClose,
  onSelectionChange,
  onMapPointsChange,
  readOnly = false,
}) {
  const groups = useMemo(
    () => citySelection?.pedidosAgrupados || [],
    [citySelection]
  );
  const selectedOrderIds = useMemo(
    () => citySelection?.IDsPedidoSelecionados || [],
    [citySelection]
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedOrderIds),
    [selectedOrderIds]
  );
  const totalOrders = useMemo(
    () => groups.reduce((total, group) => total + (group.pedidos?.length || 0), 0),
    [groups]
  );

  useEffect(() => {
    onMapPointsChange?.(buildMapPoints(groups, selectedOrderIds));
  }, [groups, onMapPointsChange, selectedOrderIds]);

  function toggleOrder(IDpedido) {
    if (readOnly) {
      return;
    }

    const next = new Set(selectedOrderIds);
    if (next.has(IDpedido)) {
      next.delete(IDpedido);
    } else {
      next.add(IDpedido);
    }
    onSelectionChange?.(Array.from(next));
  }

  function toggleGroup(group) {
    if (readOnly) {
      return;
    }

    const groupIds = (group.pedidos || []).map((pedido) => pedido.IDpedido);
    const shouldSelect = groupIds.some((IDpedido) => !selectedIdSet.has(IDpedido));
    const next = new Set(selectedOrderIds);

    groupIds.forEach((IDpedido) => {
      if (shouldSelect) {
        next.add(IDpedido);
      } else {
        next.delete(IDpedido);
      }
    });
    onSelectionChange?.(Array.from(next));
  }

  return (
    <div className="route-micro-panel">
      <div className="route-micro-header">
        <div>
          <h3>Selecao de pedidos</h3>
          <div className="route-micro-header-summary">
            <strong>
              Cidade {citySelection?.ordem}: {citySelection?.nome}
            </strong>
          </div>
          <p>{routeName ? `Rota macro: ${routeName}` : "Rota em edicao"}</p>
          <span
            className={`route-order-selection-state${
              citySelection?.salva ? " saved" : " pending"
            }`}
          >
            {citySelection?.salva
              ? "Selecao salva"
              : "Alteracoes aguardando Salvar/Alterar"}
          </span>
        </div>
        <div className="route-micro-header-actions">
          <button
            type="button"
            className="route-micro-close"
            onClick={onClose}
            aria-label="Fechar selecao de pedidos"
          >
            x
          </button>
        </div>
      </div>

      <div className="route-micro-card">
        <div className="route-micro-card-header">
          <strong>Pedidos da carga</strong>
          <span>
            {selectedOrderIds.length} de {totalOrders} selecionado(s)
          </span>
        </div>

        {groups.length === 0 ? (
          <p className="route-empty-state">Nenhum pedido disponivel nesta cidade.</p>
        ) : null}

        <div className="route-micro-order-list">
          {groups.map((group) => {
            const orders = group.pedidos || [];
            const selectedCount = orders.filter((pedido) =>
              selectedIdSet.has(pedido.IDpedido)
            ).length;
            const allSelected = orders.length > 0 && selectedCount === orders.length;

            return (
              <div
                key={`${group.IDcliente}:${group.IDendereco}`}
                className="route-micro-order-card"
              >
                <div className="route-micro-order-card-header">
                  <div>
                    <strong>{group.nomeCliente}</strong>
                    <span>{group.enderecoFormatado}</span>
                    <small>
                      {selectedCount} de {orders.length} pedido(s) selecionado(s)
                    </small>
                  </div>
                  {!readOnly ? (
                    <div className="route-micro-order-card-actions">
                      <button
                        type="button"
                        className="route-micro-inline-button"
                        onClick={() => toggleGroup(group)}
                      >
                        {allSelected ? "Retirar endereco" : "Selecionar endereco"}
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="route-micro-order-items">
                  {orders.map((pedido) => (
                    <label
                      key={pedido.IDpedido}
                      className="route-micro-order-checkbox"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIdSet.has(pedido.IDpedido)}
                        disabled={readOnly}
                        onChange={() => toggleOrder(pedido.IDpedido)}
                      />
                      <span>
                        Pedido {pedido.IDpedido} | {pedido.natureza || "Sem natureza"} |{" "}
                        {formatDate(pedido.dataPedido)} | {Number(pedido.volumes) || 0}{" "}
                        volume(s)
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {!readOnly && selectedOrderIds.length === 0 ? (
          <p className="route-order-selection-warning">
            Selecione ao menos um pedido nesta cidade ou remova a cidade da rota.
          </p>
        ) : null}
      </div>
    </div>
  );
}
